import type { GameEvent, GameEventType, Logger } from "../types";

export type EventListener = (event: GameEvent) => void;

/**
 * 简单、同步的发布/订阅事件总线。
 *
 * 事件按固定、确定性的顺序在每个系统 tick 中发出，
 * 监听器按注册顺序被调用。订阅者不得在监听器内部直接修改核心状态——仅观察。
 */
export class EventBus {
  private listeners = new Map<GameEventType, EventListener[]>();

  subscribe(type: GameEventType, listener: EventListener): () => void {
    const list = this.listeners.get(type) ?? [];
    list.push(listener);
    this.listeners.set(type, list);
    return () => {
      const current = this.listeners.get(type);
      if (!current) return;
      const idx = current.indexOf(listener);
      if (idx >= 0) current.splice(idx, 1);
    };
  }

  emit(event: GameEvent): void {
    const list = this.listeners.get(event.type);
    if (!list || list.length === 0) return;
    // Copy so that subscribe/unsubscribe during iteration is safe.
    for (const listener of list.slice()) {
      listener(event);
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}

/** 丢弃所有输出的 Logger——核心中的安全默认值（不输出到 console）。 */
export const NullLogger: Logger = {
  debug: () => undefined,
  info: () => undefined,
  warn: () => undefined,
  error: () => undefined,
};
